/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/destructuring-assignment */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { MiniLoader } from '@pwc/pwc-deals-ui';
import { Constants, USER_ROLES } from '@pwc/pwc-deals-web-common';

import UnableToLoadContent from 'features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import SyncedInfo from 'common/components/Autosync/SyncedInfo';
import { sanitize } from 'common/services/sanitizeService';
import { cleanUpHTML } from 'common/helpers/excelHiddenColumnsRemover';
import { junctionColorMapping } from 'common/helpers/junctionColorMapping';
import { updateLibraryItem } from 'features/analyses/libraryItemsSlice';

import { EContentFormats } from 'common/components/LibraryItemCardWidgetOptions';
import CellRangeLibraryItem from '../../CellRangeLibraryItem';
import { getNormalizedLibraryData } from '../../utilities';
import { ContentScalable } from '../ContentScalable';

import styles from './styles.module.scss';
import { ILibraryItemExcelRange } from './model';
import { FormatSwitcher } from '../FormatSwitcher';
import {
  getIsThirdPartyMember,
  getUserRole,
} from '../../../../../../common/redux/security/loggedInUser/loggedInUserSlice';
import NamedRangeLibraryItem from '../../NamedRangeLibraryItem';
import ChartLibraryItem from '../../ChartLibraryItem';
import PowerPointLibraryItem from '../../PowerPointLibraryItem';
import { adjustSvgDimensions } from './adjustSvgDimensions';

type TPropsContentScalable = React.ComponentProps<typeof ContentScalable>;
type TPropsFormatSwitcher = React.ComponentProps<typeof FormatSwitcher>;

export const ContentMain: React.FunctionComponent<{
  readonly content: string;
  readonly item: ILibraryItemExcelRange;
  readonly itemIsScalable: boolean;
  readonly projectID: string;
  readonly propsContentScalable: Omit<TPropsContentScalable, 'content'>;
  readonly propsFormatSwitcher: Omit<TPropsFormatSwitcher, 'type'>;
  readonly forView: { type: string };
}> = (props) => {
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const {
    content,
    itemIsScalable,
    propsContentScalable,
    item,
    projectID,
    propsFormatSwitcher,
    forView,
  } = props;
  const { type } = item || { type: '' };
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  return (
    <>
      {itemIsScalable ? (
        <ContentScalable content={content} {...propsContentScalable} />
      ) : (
        <div
          className={cn(
            'content-item-container',
            'content-item-container-scroll',
            'css-override',
            styles.contentItemContainerScroll
          )}
          // eslint-disable-next-line jam3/no-sanitizer-with-danger
          dangerouslySetInnerHTML={{
            __html: content,
          }}
        />
      )}

      {!isThirdPartyMember && (
        <SyncedInfo
          excelPPTItem={item}
          hideSwitcherForEditor
          projectId={projectID}
          isPwcUser={isPwcUser}
        />
      )}

      {forView?.type === 'freeFormAnalysisEdit' &&
        (type === Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value ||
          type === Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value) && (
          <FormatSwitcher
            type={
              itemIsScalable ? EContentFormats.Image : EContentFormats.WebTable
            }
            {...propsFormatSwitcher}
          />
        )}
    </>
  );
};

type TPropsContentMain = React.ComponentProps<typeof ContentMain>;

export enum EComponentStatesTypes {
  LoadStart = 'LoadStart',
  LoadFailure = 'LoadFailure',
  LoadSuccess = 'LoadSuccess',
  LoadEnd = 'LoadEnd',
}

interface IProps {
  readonly dispatch: ReturnType<typeof useDispatch>;
  readonly formatSwitcherOnClick?: NonNullable<
    TPropsContentMain['propsFormatSwitcher']['onClickButtons']
  >;
  readonly libraryItem: any;
  readonly onInitializeSliderProps?: NonNullable<
    TPropsContentScalable['onInitializeSliderProps']
  >;
  readonly onComponentState?: (x: { type: EComponentStatesTypes }) => void;
  readonly projectID: string;
  readonly valueSVGScalePercent: number;
  readonly forView: {
    type: string;
  };
}

interface IState {
  readonly contentToRender?: string;
  readonly error?: any;
  readonly isLoading: boolean;
  readonly itemLoaded?: ILibraryItemExcelRange;
  readonly itemIsScalable: boolean;
}

export const ExcelPPTLibraryItemCardBase: React.FunctionComponent<IProps> = ({
  dispatch,
  formatSwitcherOnClick,
  libraryItem,
  onInitializeSliderProps,
  onComponentState,
  projectID,
  valueSVGScalePercent,
  forView,
}) => {
  const [state, setState] = useState<IState>({
    contentToRender: undefined,
    error: undefined,
    isLoading: true,
    itemLoaded: undefined,
    itemIsScalable: false,
  });

  const getLibraryItemHelper = ({ excelPPTItem, item }: IState | any) => {
    switch (item.type) {
      case Constants.LIBRARY_ITEM_TYPES.EXCEL_NAMED_RANGE.value:
        return new NamedRangeLibraryItem(
          {
            data: JSON.stringify(item),
            ...item,
          },
          excelPPTItem?.formatType
        );

      case Constants.LIBRARY_ITEM_TYPES.EXCEL_CELL_RANGE.value:
        return new CellRangeLibraryItem(
          {
            data: JSON.stringify(item),
            ...item,
          },
          excelPPTItem?.formatType
        );

      case Constants.LIBRARY_ITEM_TYPES.EXCEL_CHART.value:
        return new ChartLibraryItem({
          data: JSON.stringify(item),
          ...item,
        });

      case Constants.LIBRARY_ITEM_TYPES.POWER_POINT_GROUP.value:
        return new PowerPointLibraryItem({
          data: JSON.stringify(item),
          ...item,
        });

      default:
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`invalid type: ${item?.type}`);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchInitialExcelCellRange = async ({
    item,
    projectId,
  }: {
    item: typeof libraryItem;
    projectId: string;
  }) => {
    const libraryItemHelper = getLibraryItemHelper({
      excelPPTItem: state,
      item: libraryItem,
    });

    // some very old legacy code doesn't handle Cell range corretly, set the right FormatType
    libraryItemHelper.formatType =
      libraryItemHelper.data.FormatType || libraryItemHelper.formatType;

    const payload = libraryItemHelper.getRequestPayload();
    const { service } = libraryItemHelper;
    const renderedData = await service(projectId, payload);
    const { fileName, fileContents } = renderedData;
    const itemMatched = fileContents.find(
      ({ formatType }: { formatType: string }) =>
        formatType ? formatType === libraryItemHelper.formatType : 'SVG'
    );

    const { autoSync, value: content, error } = itemMatched || { autoSync: {} };
    const worksheetName =
      libraryItemHelper?.data?.WorksheetName ||
      autoSync?.data?.worksheetName ||
      '';

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return {
      ...item,
      autoSync,
      content,
      contentsWithFormatType: undefined,
      error,
      fileName,
      fileContents,
      formatType: libraryItemHelper.formatType,
      itemMatched,
      libraryItemId: item.id,
      sharePointItemUrl: payload.SharePointItemUrl,
      slideNumber: payload.SlideNumber,
      value: content,
      worksheetName,
    };
  };

  const propsComponents = {
    FormatSwitcher: {
      onClickButtons: (evt) => {
        evt.persist();

        setState({
          ...state,
          itemIsScalable:
            (evt.target as HTMLButtonElement).getAttribute('data-name') !==
            'image',
        });
        formatSwitcherOnClick?.(evt);
      },
    } as NonNullable<TPropsContentMain['propsFormatSwitcher']>,
  };

  const loadItemExcelRange = useCallback(async () => {
    setState({
      ...state,
      isLoading: true,
    });

    try {
      if (Constants.isSliderAvailableWhitelist.includes(libraryItem.type)) {
        onComponentState?.({
          type: EComponentStatesTypes.LoadStart,
        });

        const itemLoaded: ILibraryItemExcelRange & {
          readonly fileContents: any;
          readonly itemMatched: any;
        } = await fetchInitialExcelCellRange({
          item: libraryItem,
          projectId: projectID,
        });

        const content =
          itemLoaded.content && itemLoaded.formatType === 'HTML'
            ? cleanUpHTML(junctionColorMapping(itemLoaded.content))
            : itemLoaded.content;

        setState({
          contentToRender: content ? sanitize(content) : undefined,
          error:
            !itemLoaded.content || itemLoaded.error?.isError
              ? itemLoaded.error
              : undefined,
          isLoading: false,
          itemLoaded,
          itemIsScalable: itemLoaded.formatType === 'SVG',
        });

        [
          EComponentStatesTypes.LoadSuccess,
          EComponentStatesTypes.LoadEnd,
        ].forEach((type) => {
          // this will be used by the live preview
          dispatch(
            updateLibraryItem({
              id: libraryItem.id,
              item: itemLoaded.content,
              libraryItemFormat: itemLoaded.fileContents,
            })
          );
          onComponentState?.({
            type,
          });
        });
      }
    } catch (err) {
      setState({ ...state, error: err, isLoading: false });
      [
        EComponentStatesTypes.LoadFailure,
        EComponentStatesTypes.LoadEnd,
      ].forEach((type) => {
        onComponentState?.({
          type,
        });
      });
    }
  }, [
    onComponentState,
    fetchInitialExcelCellRange,
    libraryItem,
    projectID,
    dispatch,
    state,
  ]);

  useEffect(() => {
    void loadItemExcelRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevLibraryItemRef = useRef(libraryItem);

  useEffect(() => {
    const prevProps = prevLibraryItemRef.current;

    if (
      (
        ['data', 'formatType', 'id'] as Array<keyof ILibraryItemExcelRange>
      ).some((k) => prevProps[k] !== libraryItem[k])
    ) {
      // You should define loadItemExcelRange function here or pass it via props
      void loadItemExcelRange();
    }

    prevLibraryItemRef.current = libraryItem;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryItem, state?.itemLoaded?.formatType]);

  useEffect(() => {
    dispatch(
      updateLibraryItem({
        id: libraryItem.id,
        item: adjustSvgDimensions(
          state?.itemLoaded?.content as string,
          valueSVGScalePercent / 100
        ),
        libraryItemFormat: state?.itemLoaded?.fileContents,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueSVGScalePercent]);

  const renderError = () => {
    if (!state.itemLoaded) {
      return null;
    }

    const item = state.itemLoaded as any;
    const itemData: any = getNormalizedLibraryData(state.itemLoaded);

    return (
      <UnableToLoadContent
        type={
          item.error?.type || (item.autoSync && item.autoSync.type) || item.type
        }
        data={{
          fileName: item.fileName,
          sharePointItemUrl: itemData.sharePointItemUrl || itemData.fileName,
          worksheetName: itemData.worksheetName || item.data?.worksheetName,
          chartName:
            itemData.chartName ||
            (item.autoSync && item.autoSync.data.chartName),
          groupName: item.groupName,
          slideNumber: item.slideNumber,
          name: item.autoSync && item.autoSync.data.cellRange,
        }}
        placeId="ExcelPPTLivraryItemCard"
        projectId={projectID}
        apiErrorCode={item.error?.apiErrorCode}
        errorCode={item.error?.errorCode || item.error?.apiErrorCode}
      />
    );
  };

  // eslint-disable-next-line prefer-destructuring
  const itemLoaded: ILibraryItemExcelRange = state.itemLoaded as any;

  return (
    <div
      // the old live preview needs rich-text-library-item-edit to find elements with the DOM
      className={cn('rich-text-library-item-edit-excel-ppt', styles.root, {
        [styles.loading]: state.isLoading,
        'unable-to-load-content': state.error,
      })}
    >
      {state.isLoading && <MiniLoader />}

      {!state.isLoading && state.error && state.itemLoaded && renderError()}

      {!state.isLoading && state.itemLoaded && (
        <ContentMain
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          content={state.contentToRender!}
          projectID={projectID}
          item={itemLoaded}
          itemIsScalable={state.itemIsScalable}
          propsContentScalable={{
            onInitializeSliderProps,
            valueSVGScalePercent,
          }}
          propsFormatSwitcher={propsComponents.FormatSwitcher}
          forView={forView}
        />
      )}
    </div>
  );
};

export const ExcelPPTLibraryItemCard: React.FunctionComponent<
  Omit<IProps, 'dispatch'>
> = memo((props) => {
  const dispatch = useDispatch();

  return <ExcelPPTLibraryItemCardBase dispatch={dispatch} {...props} />;
});
